import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Compass,
  Calendar,
  Sun,
  Plane,
  Banknote,
  Utensils,
  ShieldCheck,
  Sparkles,
  MapPin,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { SITE } from "@/lib/site";
import heroMexico from "@/assets/hero-mexico.webp";
import { OG_CORPORATE_IMAGE } from "@/lib/og";
import destCabo from "@/assets/dest-cabo.webp";
import destCdmx from "@/assets/dest-cdmx.webp";
import destOaxaca from "@/assets/dest-oaxaca.webp";
import destYucatan from "@/assets/dest-yucatan.webp";

/**
 * Guía de viaje a México (página `/blog`).
 * Página editorial/marketing pensada para convertir lectores en reservas.
 * El contenido es original, basado en información pública de Visit Mexico,
 * SECTUR y guías de operadores locales (oct 2025).
 */

export const Route = createFileRoute("/blog")({
  head: () => ({
    meta: [
      { title: `Guía de viaje a México 2026 — ${SITE.brand.name}` },
      {
        name: "description",
        content:
          "Todo lo que necesitas saber para viajar a México: mejores meses, presupuesto, gastronomía, transporte, seguridad y los destinos imperdibles. Reserva tours auténticos con Mostrando México.",
      },
      { property: "og:title", content: `Guía de viaje a México 2026 — ${SITE.brand.name}` },
      { property: "og:description", content: "Itinerarios, presupuesto y consejos locales para descubrir México como un experto." },
      { property: "og:url", content: "https://www.mostrandomexico.mx/blog" },
      { property: "og:image", content: OG_CORPORATE_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_CORPORATE_IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://www.mostrandomexico.mx/blog" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Guía de viaje a México 2026",
          image: [heroMexico],
          author: { "@type": "Organization", name: SITE.brand.name },
          publisher: { "@type": "Organization", name: SITE.brand.name },
          description:
            "Guía completa para viajar a México: cuándo ir, cuánto cuesta, qué comer, qué hacer y cómo reservar tours auténticos con operadores locales.",
        }),
      },
    ],
  }),
  component: GuidePage,
});

const STATS = [
  { value: "42M+", label: "Turistas al año" },
  { value: "35", label: "Sitios Patrimonio UNESCO" },
  { value: "11 000+", label: "km de costa" },
  { value: "68", label: "Lenguas indígenas vivas" },
];

const DESTINATIONS = [
  {
    name: "Riviera Maya · Cancún",
    image: destYucatan,
    vibe: "Playas turquesa · cenotes · ruinas mayas",
    duration: "5–7 días",
    best: "Nov – Abr",
    pitch:
      "El combo perfecto entre playa de revista, arqueología y vida nocturna. Base ideal para Tulum, Holbox, Cozumel y Chichén Itzá.",
    bullets: ["Snorkel en arrecifes de Cozumel", "Cenotes sagrados de la Riviera", "Chichén Itzá al amanecer"],
    href: "/destinos/quintana-roo",
  },
  {
    name: "Oaxaca",
    image: destOaxaca,
    vibe: "Cultura viva · gastronomía · pueblos mágicos",
    duration: "4–6 días",
    best: "Oct – May",
    pitch:
      "Capital culinaria de América Latina. Mercados sin filtros, mezcal artesanal y comunidades zapotecas que custodian milenios de tradición.",
    bullets: ["Hierve el Agua y Mitla", "Ruta del mezcal en Matatlán", "Día de Muertos auténtico (oct–nov)"],
    href: "/destinos/oaxaca",
  },
  {
    name: "Los Cabos",
    image: destCabo,
    vibe: "Mar de Cortés · resort vibes · aventura",
    duration: "4–5 días",
    best: "Oct – Jun",
    pitch:
      "Donde el desierto se zambulle en el Pacífico. Avistamiento de ballenas, pesca de altura y atardeceres entre formaciones rocosas únicas.",
    bullets: ["El Arco y Playa del Amor", "Ballenas grises (ene–mar)", "Cata de tequila y mariscos"],
    href: "/destinos/baja-california-sur",
  },
  {
    name: "Ciudad de México",
    image: destCdmx,
    vibe: "Metrópoli cultural · museos · arquitectura",
    duration: "3–5 días",
    best: "Todo el año",
    pitch:
      "La capital con más museos del continente. Tacos al pastor a las 2 AM, arte de Frida y pirámides a una hora en auto.",
    bullets: ["Teotihuacán en globo", "Xochimilco con mariachi", "Barrios Roma y Condesa"],
    href: "/destinos/cdmx",
  },
];

const MONTHS = [
  { m: "Ene", t: "Seco · 20–28°C", note: "Temporada alta. Ballenas en Baja." },
  { m: "Feb", t: "Seco · 21–28°C", note: "Carnaval en Mazatlán y Veracruz." },
  { m: "Mar", t: "Seco · 23–30°C", note: "Equinoccio en Chichén Itzá." },
  { m: "Abr", t: "Templado · 25–32°C", note: "Semana Santa, reservar con tiempo." },
  { m: "May", t: "Caluroso · 28–34°C", note: "Mejor precio en playa." },
  { m: "Jun", t: "Lluvias · 27–32°C", note: "Tortugas marinas anidando." },
  { m: "Jul", t: "Lluvias · 27–33°C", note: "Guelaguetza en Oaxaca." },
  { m: "Ago", t: "Lluvias · 27–33°C", note: "Tiburón ballena en Holbox." },
  { m: "Sep", t: "Lluvias · 26–31°C", note: "Fiestas patrias 15–16 sep." },
  { m: "Oct", t: "Templado · 25–30°C", note: "Hombro: clima ideal, menos gente." },
  { m: "Nov", t: "Seco · 23–29°C", note: "Día de Muertos 1–2 nov." },
  { m: "Dic", t: "Seco · 22–28°C", note: "Posadas y temporada alta navideña." },
];

const BUDGET = [
  {
    tier: "Mochilero",
    daily: "USD 35–55",
    color: "from-emerald-500/15 to-emerald-500/0",
    items: ["Hostales y posadas", "Comida en mercados", "Camiones y colectivos", "1–2 tours grupales / semana"],
  },
  {
    tier: "Cómodo",
    daily: "USD 90–150",
    color: "from-primary/20 to-primary/0",
    items: ["Hoteles boutique 3–4★", "Restaurantes locales", "Uber + traslados", "Tour privado puntual"],
  },
  {
    tier: "Premium",
    daily: "USD 250+",
    color: "from-foreground/20 to-foreground/0",
    items: ["Resorts y casas de diseño", "Cenas de autor", "Vuelos internos", "Tours privados con concierge"],
  },
];

const FOOD = [
  { dish: "Tacos al pastor", where: "CDMX", why: "Adobo de chile, piña y trompo a la leña — irrepetible." },
  { dish: "Cochinita pibil", where: "Yucatán", why: "Cerdo marinado en achiote, cocido bajo tierra." },
  { dish: "Mole negro", where: "Oaxaca", why: "Hasta 30 ingredientes; el plato más complejo del país." },
  { dish: "Aguachile", where: "Sinaloa · Nayarit", why: "Camarón crudo con chile y limón. Fresco, picante, adictivo." },
  { dish: "Birria", where: "Jalisco", why: "Estofado de res con tortilla doradita y consomé." },
  { dish: "Pescado zarandeado", where: "Riviera Nayarit", why: "Pescado abierto a la leña con salsa de chiles." },
];

const TIPS = [
  { title: "Visa y entrada", body: "Más de 70 nacionalidades entran sin visa hasta 180 días. Llena el FMM digital antes de aterrizar." },
  { title: "Moneda", body: "Peso mexicano (MXN). Acepta tarjeta en ciudades; lleva efectivo para mercados y propinas (10–15%)." },
  { title: "Idioma", body: "Español. En zonas turísticas hablan inglés. Aprende 'gracias', 'cuánto cuesta' y 'la cuenta, por favor'." },
  { title: "Salud", body: "Agua embotellada siempre. Vacunas estándar; en selva considera repelente con DEET." },
  { title: "Seguridad", body: "Viaja por zonas turísticas, usa Uber o DiDi en ciudades grandes y evita carretera de noche." },
  { title: "Conectividad", body: "eSIM con Telcel o AT&T cubre prácticamente todo el país. Wi-Fi gratis en cafés y hoteles." },
];

const ITINERARY = [
  { day: "Día 1–2", title: "Cancún & Riviera", desc: "Llegada, descanso en playa, tour de cenotes y snorkel en arrecife." },
  { day: "Día 3", title: "Chichén Itzá + Valladolid", desc: "Pirámide al amanecer, cenote Suytun y comida yucateca." },
  { day: "Día 4–5", title: "Tulum & Sian Ka'an", desc: "Ruinas frente al mar, reserva de la biosfera y cabañas eco-chic." },
  { day: "Día 6", title: "Cozumel o Holbox", desc: "Día de buceo en arrecife o tiburón ballena (jun–sep)." },
  { day: "Día 7", title: "CDMX exprés", desc: "Vuelo corto, tacos al pastor y museos en Roma-Condesa." },
];

function GuidePage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      {/* Hero */}
      <header className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img decoding="async" fetchPriority="high"
            src={heroMexico}
            alt="Paisaje colorido de México con arquitectura colonial y vegetación tropical"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground/70 via-foreground/55 to-foreground/85" />
        </div>
        <div className="mx-auto max-w-6xl px-4 py-20 text-background sm:px-6 lg:px-8 lg:py-28">
          <p className="inline-flex items-center gap-2 rounded-full border border-background/30 bg-background/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] backdrop-blur">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> Guía de viaje 2026
          </p>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            México, sin filtros: la guía que sí te lleva más allá del resort.
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-background/85 sm:text-lg">
            Itinerarios, presupuesto realista, gastronomía y los rincones que solo conocen los locales.
            Curada por nuestros guías en Cancún, CDMX y Oaxaca.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to="/tours"
              className="inline-flex h-12 items-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-glow transition hover:bg-primary-glow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Ver tours auténticos <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <Link
              to="/disena-mi-viaje"
              className="inline-flex h-12 items-center gap-2 rounded-full border border-background/40 bg-background/10 px-6 text-sm font-semibold text-background backdrop-blur transition hover:bg-background/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background"
            >
              ✨ Diseña mi viaje con Maya IA
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Stats */}
        <section
          aria-label="México en números"
          className="relative z-10 -mt-10 grid gap-3 rounded-2xl border border-border bg-card p-6 shadow-elevated sm:-mt-12 sm:grid-cols-4"
        >
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-display text-3xl font-bold text-primary">{s.value}</p>
              <p className="mt-1 text-xs uppercase tracking-[0.14em] text-muted-foreground">
                {s.label}
              </p>
            </div>
          ))}
        </section>

        {/* TOC */}
        <nav aria-label="Tabla de contenidos" className="mt-12">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            En esta guía
          </p>
          <ul className="mt-3 flex flex-wrap gap-2">
            {[
              ["destinos", "Destinos imperdibles"],
              ["cuando", "Mejor época"],
              ["presupuesto", "Presupuesto"],
              ["gastronomia", "Qué comer"],
              ["itinerario", "Itinerario 7 días"],
              ["consejos", "Tips prácticos"],
            ].map(([id, label]) => (
              <li key={id}>
                <a
                  href={`#${id}`}
                  className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground/80 transition hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Destinos */}
        <Section id="destinos" eyebrow="Destinos" title="Cuatro Méxicos en un solo viaje" icon={Compass}>
          <p className="max-w-3xl text-muted-foreground">
            México es un mosaico de climas, culturas y cocinas. Estas son las cuatro regiones que
            recomendamos a quien viene por primera vez —cada una merece su propio viaje.
          </p>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {DESTINATIONS.map((d) => (
              <article
                key={d.name}
                className="group overflow-hidden rounded-3xl border border-border bg-card shadow-card transition hover:-translate-y-0.5 hover:shadow-elevated"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={d.image}
                    alt={`Paisaje de ${d.name}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                  />
                  <span className="absolute left-4 top-4 inline-flex items-center gap-1 rounded-full bg-background/95 px-3 py-1 text-[11px] font-semibold text-foreground shadow-sm">
                    <MapPin className="h-3 w-3 text-primary" aria-hidden="true" /> {d.vibe}
                  </span>
                </div>
                <div className="p-6">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-display text-2xl font-semibold">{d.name}</h3>
                    <span className="text-xs font-semibold text-muted-foreground">
                      {d.duration} · {d.best}
                    </span>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{d.pitch}</p>
                  <ul className="mt-4 space-y-1.5 text-sm">
                    {d.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" aria-hidden="true" />
                        <span className="text-foreground/85">{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={d.href as any}
                    className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                  >
                    Explorar tours <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </Section>

        {/* Cuando ir */}
        <Section id="cuando" eyebrow="Mejor época" title="¿Cuándo visitar México?" icon={Sun}>
          <p className="max-w-3xl text-muted-foreground">
            México se viaja todo el año, pero cada mes tiene su personalidad. Los meses{" "}
            <strong className="text-foreground">octubre, noviembre y abril</strong> ofrecen el
            equilibrio perfecto entre clima, precio y menos multitudes.
          </p>
          <div className="mt-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {MONTHS.map((m) => (
              <div
                key={m.m}
                className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 shadow-card"
              >
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 font-display text-sm font-bold text-primary">
                  {m.m}
                </span>
                <div>
                  <p className="text-xs font-semibold text-foreground">{m.t}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{m.note}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Presupuesto */}
        <Section id="presupuesto" eyebrow="Presupuesto" title="Cuánto cuesta viajar a México" icon={Banknote}>
          <p className="max-w-3xl text-muted-foreground">
            Para una pareja extranjera, un viaje de 7 días por la Riviera Maya cuesta entre{" "}
            <strong className="text-foreground">USD 900 y USD 3 500</strong> sin vuelo internacional.
            Aquí lo desglosamos por estilo:
          </p>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {BUDGET.map((b) => (
              <div
                key={b.tier}
                className={`relative overflow-hidden rounded-2xl border border-border bg-gradient-to-b ${b.color} bg-card p-6 shadow-card`}
              >
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  {b.tier}
                </p>
                <p className="mt-2 font-display text-3xl font-bold text-foreground">{b.daily}</p>
                <p className="text-xs text-muted-foreground">por persona / día</p>
                <ul className="mt-4 space-y-2 text-sm">
                  {b.items.map((i) => (
                    <li key={i} className="flex items-start gap-2 text-foreground/80">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" aria-hidden="true" />
                      {i}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            💡 Reserva tours con códigos <strong>NACIONAL</strong> (−20% para mexicanos) y{" "}
            <strong>TRAVEL</strong> (−10% internacional con 7 días de anticipación).
          </p>
        </Section>

        {/* Gastronomía */}
        <Section id="gastronomia" eyebrow="Gastronomía" title="Lo que tienes que comer" icon={Utensils}>
          <p className="max-w-3xl text-muted-foreground">
            La cocina mexicana es Patrimonio Inmaterial de la Humanidad por la UNESCO desde 2010.
            Estos seis platillos son tu mejor punto de entrada:
          </p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FOOD.map((f) => (
              <div key={f.dish} className="rounded-2xl border border-border bg-card p-5 shadow-card">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
                  {f.where}
                </p>
                <h3 className="mt-1 font-display text-xl font-semibold">{f.dish}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.why}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Itinerario */}
        <Section
          id="itinerario"
          eyebrow="Plantilla"
          title="Itinerario 7 días — el ‘best of México'"
          icon={Calendar}
        >
          <ol className="mt-6 space-y-3">
            {ITINERARY.map((it, i) => (
              <li
                key={it.day}
                className="relative grid gap-3 rounded-2xl border border-border bg-card p-5 shadow-card sm:grid-cols-[120px_1fr]"
              >
                <div>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                    {i + 1}
                  </span>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                    {it.day}
                  </p>
                </div>
                <div>
                  <h3 className="font-display text-lg font-semibold">{it.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{it.desc}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-6 rounded-2xl border border-primary/30 bg-accent/40 p-6">
            <p className="font-display text-lg font-semibold text-accent-foreground">
              ¿Quieres este itinerario a tu medida?
            </p>
            <p className="mt-1 text-sm text-accent-foreground/80">
              Maya, nuestra concierge IA, lo personaliza en 30 segundos con tu fecha, presupuesto e
              intereses —usando tours reales de operadores locales.
            </p>
            <Link
              to="/disena-mi-viaje"
              className="mt-4 inline-flex h-11 items-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-glow transition hover:bg-primary-glow"
            >
              <Sparkles className="h-4 w-4" aria-hidden="true" /> Diseñar mi viaje
            </Link>
          </div>
        </Section>

        {/* Tips */}
        <Section id="consejos" eyebrow="Antes de salir" title="Lo que ojalá supieras antes" icon={ShieldCheck}>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {TIPS.map((t) => (
              <div key={t.title} className="rounded-2xl border border-border bg-card p-5 shadow-card">
                <h3 className="font-display text-base font-semibold">{t.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">{t.body}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* CTA final */}
        <section className="my-16 overflow-hidden rounded-3xl border border-border bg-foreground p-8 text-background shadow-elevated sm:p-12">
          <div className="grid items-center gap-6 lg:grid-cols-[1.4fr_1fr]">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full border border-background/20 bg-background/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em]">
                <Plane className="h-3.5 w-3.5" aria-hidden="true" /> Listo para reservar
              </p>
              <h2 className="mt-4 font-display text-3xl font-semibold sm:text-4xl">
                Convierte esta guía en un viaje real.
              </h2>
              <p className="mt-3 max-w-xl text-sm text-background/80 sm:text-base">
                Tours operados por equipos locales, cambio de fecha gratis hasta 24 h
                antes y asesoría 24/7 por WhatsApp.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
              <Link
                to="/tours"
                className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-glow transition hover:bg-primary-glow"
              >
                Ver tours <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <a
                href={SITE.contact.whatsappPrettyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-full border border-background/40 bg-background/10 px-5 text-sm font-semibold text-background transition hover:bg-background/20"
              >
                Hablar por WhatsApp
              </a>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

function Section({
  id,
  eyebrow,
  title,
  icon: Icon,
  children,
}: {
  id: string;
  eyebrow: string;
  title: string;
  icon: typeof Compass;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24 py-14">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-primary">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            {eyebrow}
          </p>
          <h2 className="font-display text-3xl font-semibold sm:text-4xl">{title}</h2>
        </div>
      </div>
      <div className="mt-6">{children}</div>
    </section>
  );
}
